import { formatOptionLabel } from "@src/core/engine/helpers/fieldDefinitions/getOptionsFieldDefinition";
import { PipeSeries } from "@src/core/engine/pipes/buildPipeSeries";
import { normalizeCustomSeries } from "@src/core/engine/pipes/normalizeCustomSeries";
import { pipesRepository } from "@src/core/engine/pipes/pipesRepository";
import { useStore } from "@tanstack/react-form";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { usePipeSeriesDataSuspense } from "../hooks/usePipeSeriesData";
import { usePipeSeriesForm } from "../hooks/usePipeSeriesForm";

type PipeSelectProps = {
  form: ReturnType<typeof usePipeSeriesForm>["form"];
};

export function PipeSelect({ form }: PipeSelectProps) {
  const { t } = useTranslation("settings");
  const { customPipeSeries } = usePipeSeriesDataSuspense();
  const allSeries: PipeSeries[] = useMemo(
    () => [
      ...normalizeCustomSeries(customPipeSeries),
      ...pipesRepository.getAllPipeSeries(),
    ],
    [customPipeSeries],
  );
  const selectedSeries = useStore(
    form.store,
    (state) =>
      allSeries.find(({ id }) => id === state.values.pipeSeries) ??
      allSeries[0],
  );
  const pipeOptions =
    selectedSeries.pipes.map((pipe) => ({
      default: pipe.id === selectedSeries?.defaultPipe?.id,
      id: pipe.id,
      label: pipe.label,
      translationKey: pipe.translationKey,
    })) ?? [];

  return (
    <form.AppField name="pipes">
      {(pipesField) => (
        <pipesField.Select label={t("pipeSeries.pipes")}>
          {pipeOptions.map((pipe) => (
            <option key={pipe.id} value={pipe.id}>
              {formatOptionLabel(pipe, t)}{" "}
              {pipe.default ? t("pipeSeries.defaultOption") : ""}
            </option>
          ))}
        </pipesField.Select>
      )}
    </form.AppField>
  );
}
